import { useState } from 'react'
import * as Tabs from '@radix-ui/react-tabs'
import Typography from 'src/components/atoms/Typography'
import SubscriptionCard from 'src/components/molecules/SubscriptionCard'
import { PaymentMethod } from 'src/components/molecules/PaymentMethod'
import PaymentTable from './PaymentTable'
import { useGetPaymentMethods } from 'src/enviroment/api/services/paymentMethodsApi'
import { format } from 'date-fns'
import { useGetSubscription, useGetSubscriptionPlans } from 'src/enviroment/api/services/subscriptionsApi'
import AddNewPaymentMethod from 'src/components/organisms/AddPaymentMethod '
import { capitalizeText } from 'src/enviroment/lib/utils'
import CancelSubscriptionDialog from 'src/components/organisms/dialogs/CancelSubscriptionDialog'
import { IconLoader } from '@tabler/icons-react'
import { BillingIntervalType } from '../../types/subscription'
import BillingIntervalToggle from '../../components/atoms/BillingIntervalToggle'
import EnterpriseDemoDialog from '../../components/organisms/dialogs/EnterpriseDemoDialog'

const SubscriptionPage = () => {
  const [billingInterval, setBillingInterval] = useState<BillingIntervalType>(BillingIntervalType.MONTH)
  const [isEnterpriseDemoOpen, setIsEnterpriseDemoOpen] = useState(false)
  const [isFeaturesExpanded, setIsFeaturesExpanded] = useState(false)

  const { data: methods } = useGetPaymentMethods()
  const { data: subscription, isLoading: isSubscriptionLoading } = useGetSubscription()
  const { data: subscriptions, isLoading } = useGetSubscriptionPlans({ interval: billingInterval })

  return (
    <div className="min-h-screen bg-gray-900 p-4 sm:p-6 md:p-8">
      <Tabs.Root defaultValue="subscription" className="flex flex-col">
        <Tabs.List className="mb-6 flex space-x-8">
          <Tabs.Trigger
            value="subscription"
            className="rounded-lg px-6 py-3 text-white outline-none transition-all
                       data-[state=active]:bg-dark-green-400 data-[state=inactive]:bg-transparent
                       data-[state=active]:shadow-lg"
          >
            Subscription
          </Tabs.Trigger>
          <Tabs.Trigger
            value="billing"
            className="rounded-lg px-6 py-3 text-white outline-none transition-all
                       data-[state=active]:bg-dark-green-400 data-[state=inactive]:bg-transparent
                       data-[state=active]:shadow-lg"
          >
            Billing Information
          </Tabs.Trigger>
        </Tabs.List>

        <Tabs.Content value="subscription" className="outline-none">
          <div className="rounded-lg bg-[#27292C] p-8">
            <div className="mb-8 text-center md:text-left">
              <Typography variant="xl" className="text-light-green-50">
                Choose a Plan That Fits
              </Typography>
              <Typography variant="base" className="mt-3 text-light-green-50">
                To change your plan, choose another from the categories.
              </Typography>
            </div>

            <div className="mb-8">
              <BillingIntervalToggle value={billingInterval} onChange={setBillingInterval} />
            </div>

            <div className="relative min-h-[450px]">
              {isLoading ? (
                <div className="absolute inset-0 flex items-center justify-center text-light-green-500">
                  <IconLoader className="animate-spin" size={25} />
                </div>
              ) : (
                <div className="flex flex-wrap items-stretch justify-center gap-4">
                  {subscriptions?.plans.map((plan) => (
                    <SubscriptionCard
                      key={`${plan.plan}-${plan.interval}`}
                      subscription={plan}
                      isCurrentPlan={
                        subscription?.currentPlan === 'PRO'
                          ? plan.plan === subscription?.currentPlan && plan.interval === subscription?.interval
                          : plan.plan === subscription?.currentPlan
                      }
                      isExpanded={isFeaturesExpanded}
                      onToggleExpand={() => setIsFeaturesExpanded((prev) => !prev)}
                      onEnterpriseClick={() => setIsEnterpriseDemoOpen(true)}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </Tabs.Content>

        <Tabs.Content value="billing" className="outline-none">
          <div className="mb-4 rounded-lg bg-[#27292C] p-8">
            <Typography variant="xl" className="mb-6 text-light-green-50">
              Current Subscription
            </Typography>
            <div className="mb-6 grid grid-cols-2 gap-4">
              <div>
                <Typography variant="sm-medium" className="text-gray-100">
                  Current Plan
                </Typography>
                <Typography variant="base" className="text-light-green-50">
                  {capitalizeText(subscription?.currentPlan)}
                </Typography>
              </div>
              <div>
                <Typography variant="sm-medium" className="text-gray-100">
                  Status
                </Typography>
                <Typography variant="base" className="text-light-green-50">
                  {capitalizeText(subscription?.status)}
                </Typography>
              </div>
              {subscription?.interval && (
                <div>
                  <Typography variant="sm-medium" className="text-gray-100">
                    Billing Cycle
                  </Typography>
                  <Typography variant="base" className="text-light-green-50">
                    {subscription.interval === 'month' ? 'Monthly' : 'Yearly'}
                  </Typography>
                </div>
              )}
              {subscription?.currentPeriodEnd && (
                <div>
                  <Typography variant="sm-medium" className="text-gray-100">
                    Next billing date
                  </Typography>
                  <Typography variant="base" className="text-light-green-50">
                    {format(new Date(subscription.currentPeriodEnd), 'MMMM dd, yyyy')}
                  </Typography>
                </div>
              )}
              <div>
                <Typography variant="sm-medium" className="text-gray-100">
                  Auto-renewal
                </Typography>
                <Typography variant="base" className="text-light-green-50">
                  {subscription?.cancelAtPeriodEnd ? 'Off' : 'On'}
                </Typography>
              </div>
            </div>
            {/* For now allow canceling only PRO plan, ENTERPRISE plan flow TBD */}
            {!isSubscriptionLoading && subscription && subscription.currentPlan === 'PRO' && (
              <CancelSubscriptionDialog />
            )}
          </div>

          <div className="rounded-lg bg-[#27292C] p-8">
            <Typography variant="xl" className="text-light-green-50">
              Payment Method
            </Typography>
            <div className="mb-4 mt-4 flex flex-col space-y-4">
              {methods?.map((method) => (
                <PaymentMethod key={method.id} method={method} isDelete />
              ))}
            </div>
            <AddNewPaymentMethod />
          </div>

          <div className="mt-5 rounded-lg bg-[#27292C] p-8">
            <Typography variant="xl" className="text-light-green-50">
              Payment History
            </Typography>
            <div className="mt-4">
              <PaymentTable />
            </div>
          </div>
        </Tabs.Content>

        <EnterpriseDemoDialog isOpen={isEnterpriseDemoOpen} onClose={() => setIsEnterpriseDemoOpen(false)} />
      </Tabs.Root>
    </div>
  )
}

export default SubscriptionPage
